import { useEffect, useRef } from 'react'

import { useAuth } from 'auth/AuthContext'
import { IconButton } from 'components/ui'
import { tx } from 'lib/i18n'

import { useRegion } from '../context/RegionContext'
import { useTheme } from '../context/ThemeContext'
import { Breadcrumb } from '../molecules/Breadcrumb'
import { ProjectSwitcher } from '../molecules/ProjectSwitcher'
import { NotificationBell } from './NotificationBell'

const canSeeAlerts = (isAdmin: boolean, roles: string[]) => isAdmin || roles.includes('PROJECT_MANAGER')

const SOON_LABEL = 'قريباً'

export const Topbar = ({ onToggleSidebar, sidebarOpen }: { onToggleSidebar: () => void; sidebarOpen: boolean }) => {
  const { user, signOut } = useAuth()
  const { locale, setLocale } = useRegion()
  const { resolvedDark, setTheme } = useTheme()
  const roles = user?.memberships.map((membership) => membership.role) ?? []
  const headerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const header = headerRef.current
    const shell = header?.closest('.shell')
    if (!header || !(shell instanceof HTMLElement)) return
    const apply = () => shell.style.setProperty('--shell-bar-height', `${header.offsetHeight}px`)
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(header)
    return () => observer.disconnect()
  }, [])

  return (
    <header className="shell__topbar" ref={headerRef}>
      <div className="shell__topbar-start">
        <IconButton
          id="sidebar-toggle"
          icon="panelLeft"
          label={
            sidebarOpen
              ? tx(locale, 'إغلاق القائمة', 'Close the menu')
              : tx(locale, 'فتح القائمة', 'Open the menu')
          }
          aria-expanded={sidebarOpen}
          aria-controls="app-sidebar"
          onClick={onToggleSidebar}
        />
        <Breadcrumb />
      </div>
      <div className="shell__topbar-center">
        <input className="input shell__search" placeholder={tx(locale, `بحث… (${SOON_LABEL})`, 'Search… (Soon)')} disabled aria-disabled="true" tabIndex={-1} />
      </div>
      <div className="shell__topbar-end">
        <ProjectSwitcher />
        <div className="shell__lang" role="group" aria-label={tx(locale, 'اللغة', 'Language')}>
          <button
            type="button"
            className={locale === 'en' ? 'shell__lang-btn is-active' : 'shell__lang-btn'}
            aria-pressed={locale === 'en'}
            onClick={() => setLocale('en')}
          >
            EN
          </button>
          <button
            type="button"
            className={locale === 'ar' ? 'shell__lang-btn is-active' : 'shell__lang-btn'}
            lang="ar"
            aria-pressed={locale === 'ar'}
            onClick={() => setLocale('ar')}
          >
            ع
          </button>
        </div>
        <IconButton
          className="shell__theme"
          icon={resolvedDark ? 'moon' : 'sun'}
          label={resolvedDark ? tx(locale, 'التبديل إلى المظهر الفاتح', 'Switch to light theme') : tx(locale, 'التبديل إلى المظهر الداكن', 'Switch to dark theme')}
          onClick={() => setTheme(resolvedDark ? 'light' : 'dark')}
        />
        {user && canSeeAlerts(user.isAdmin, roles) ? <NotificationBell /> : null}
        <IconButton className="shell__signout" icon="logout" label={tx(locale, 'تسجيل الخروج', 'Sign out')} onClick={signOut} />
      </div>
    </header>
  )
}
